import { input, type QueryList, type SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  ViewChildren,
  output,
} from '@angular/core';

import type { UserBase } from '@core/account-types';
import type { Attachment } from '@core/attachments-types';
import type {
  BaseNote,
  NoteDeleteAttachmentEvent,
  NoteDiscardEvent,
  NoteEditEvent,
  NoteId,
  NoteLocalStorageKeyBuilder,
  NoteReplyChangesEvent,
  NoteReplyClickEvent,
  NoteReplyTypingStartEvent,
  NoteSave,
  NoteUploadAttachmentEvent,
  SavedNoteId,
} from '@core/notes-types';
import type { MentionElement } from '@core/prosemirror-legacy';

import type { NoteAvatarAuthor } from '../note-avatar/note-avatar.types';
import { type VisibilitySelector } from '../note-form/note-form.component';
import { NoteItemComponent } from '../note-item/note-item.component';
import { NoteTypingIndicatorMessageComponent } from '../note-typing-indicator-message/note-typing-indicator-message.component';

@Component({
  selector: 'rt-notes-list',
  templateUrl: './notes-list.component.html',
  styleUrls: ['./notes-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NoteTypingIndicatorMessageComponent, NoteItemComponent],
})
export class NotesListComponent<Note extends BaseNote<Note>> {
  @Input() public notes: Note[];
  @Input()
  public currentlyTypedNotes: Array<{
    admin: UserBase;
    replyToId?: number | null;
  }> = [];

  @Input() public canDeleteAttachments: boolean = false;
  @Input() public canHighLevelDataAccess: boolean = false;
  @Input() public isReadOnly: boolean = false;

  @Input() public notesBeingSaved: NoteId[] = [];
  @Input() public notesBeingDeleted: NoteId[] = [];
  @Input() public notesBeingEdited: NoteId[] = [];
  @Input() public notesBeingRepliedTo: NoteId[] = [];

  @Input() public noteAttachmentsBeingUploaded: { [key: string]: string[] } = {};
  @Input() public noteAttachmentsTemp: Record<
    string,
    Array<{ uuid: string; attachment: Attachment }>
  >;

  public isOwnerFn = input.required<(note: Note) => boolean>();
  public getOwnerFn = input.required<(note: Note) => NoteAvatarAuthor | null>();
  public localStorageKeyBuilder = input.required<NoteLocalStorageKeyBuilder<Note>>();
  public noteToMention = input.required<(note: Note) => MentionElement | null>();
  public isAutomatedActionNoteFn = input.required<(note: Note) => boolean>();
  public visibilitySelectorComponent = input<VisibilitySelector>();
  public visibilityComponentInputs = input<Partial<InstanceType<VisibilitySelector>>>();
  public canReply = input(true);
  public canPin = input(true);
  public hasFileUpload = input(true);
  public hasReactions = input(true);

  @Input() public scrollToNoteId: number;

  public replyTypingStart = output<NoteReplyTypingStartEvent>();
  public replyTypingFinish = output<void>();

  public noteDiscardClick = output<NoteDiscardEvent>();
  public noteEditClick = output<NoteEditEvent>();
  public noteReplyClick = output<NoteReplyClickEvent>();
  public noteSaveClick = output<NoteSave<Note>>();
  public noteChecklistClick = output<{
    id: number;
    bodyJson: object;
  }>();
  public noteDeleteClick = output<SavedNoteId>();
  public notePinClick = output<SavedNoteId>();
  public noteUnpinClick = output<SavedNoteId>();
  public uploadAttachmentClick = output<NoteUploadAttachmentEvent>();
  public deleteAttachmentClick = output<NoteDeleteAttachmentEvent<Note>>();

  public cachedReply: NoteReplyChangesEvent | null = null;
  public sortedNotes: Note[];
  public allNotesFlat: Note[];

  @ViewChildren('noteRef', { read: ElementRef })
  private noteItems: QueryList<ElementRef>;

  private sortFunctions = {
    sortReplies: (a: Note, b: Note) => (a.createdAt > b.createdAt ? 1 : -1),
    sortMainNotes: (a: Note, b: Note) => {
      if (a.pinnedAt && !b.pinnedAt) return -1;
      if (!a.pinnedAt && b.pinnedAt) return 1;
      if (a.pinnedAt && b.pinnedAt) return a.pinnedAt > b.pinnedAt ? 1 : -1;

      return a.createdAt > b.createdAt ? -1 : 1;
    },
  };

  public ngAfterViewInit(): void {
    const noteScrollToElement = this.noteItems.find(
      noteItem =>
        noteItem && parseInt(noteItem.nativeElement.dataset.id, 10) === this.scrollToNoteId,
    );
    if (noteScrollToElement) {
      noteScrollToElement.nativeElement.scrollIntoView();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.notes) {
      this.sortedNotes = this.notes
        .map(note => ({
          ...note,
          replies: [...note.replies].sort(this.sortFunctions.sortReplies),
        }))
        .sort(this.sortFunctions.sortMainNotes);

      this.allNotesFlat = this.flattenNotes(this.sortedNotes);
    }
  }

  public setCachedReply({ replyToId, value }: NoteReplyChangesEvent): void {
    if (value === null) {
      this.cachedReply = null;
    } else {
      this.cachedReply = { replyToId, value };
    }
  }

  public trackByNote(index: number, note: Note): string | number {
    return note?.id || JSON.stringify(note);
  }

  public get typingAdmins(): UserBase[] {
    return this.currentlyTypedNotes.filter(note => !note.replyToId).map(note => note.admin);
  }

  private flattenNotes(notes: Note[]): Note[] {
    if (!notes || !notes.length) return [];

    return notes.flatMap(note => [
      { ...note, replies: [] },
      ...this.flattenNotes(<Note[]>note.replies),
    ]);
  }
}
